import React from "react";
import { Brain, ChartLine, Atom, GitBranch } from "@phosphor-icons/react";
import { useI18n } from "../../i18n/I18nContext";

export const TechCore = () => {
  const { t } = useI18n();

  return (
    <section id="tech" data-testid="tech-section" className="relative py-24 sm:py-32">
      <div className="absolute inset-0 grid-bg opacity-25 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-8 mb-14">
          <div className="lg:col-span-7">
            <div className="mono text-xs uppercase tracking-[0.22em] text-brand-cyan mb-3">
              {t.techCore.sectionNum}
            </div>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
              {t.techCore.headlinePart1}
              <br />
              <span className="text-brand-cyan">{t.techCore.headlinePart2}</span>
            </h2>
            <p className="mt-6 text-muted text-base leading-relaxed max-w-2xl">
              {t.techCore.subtext}
            </p>
          </div>
        </div>

        {/* Bento */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Formula card — span 7 */}
          <div className="lg:col-span-7 glass-strong rounded-2xl p-8 sm:p-10 relative overflow-hidden">
            <div className="mono text-[10px] uppercase tracking-[0.22em] text-brand-cyan mb-6">
              {t.techCore.scoringTitle}
            </div>
            <div className="relative">
              <div className="font-heading font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-none">
                Score ={" "}
                <span className="text-brand-cyan text-glow-cyan">{t.techCore.formula.cei}</span>{" "}
                ×{" "}
                <span className="text-ink-text">(1 −</span>{" "}
                <span className="text-brand text-glow-blue">{t.techCore.formula.zcr}</span>
                <span className="text-ink-text">)</span>
              </div>
              <div className="absolute -bottom-3 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-cyan/60 to-transparent" />
            </div>

            <div className="mt-10 grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl bg-ink-surface/60 border border-ink-text/5 p-5">
                <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-brand-cyan mb-3">
                  <Atom size={14} weight="bold" /> {t.techCore.formula.cei}
                </div>
                <div className="font-heading font-semibold text-lg mb-2">
                  {t.techCore.factors.ceiTitle}
                </div>
                <div className="text-sm text-muted leading-relaxed">
                  {t.techCore.factors.ceiDesc}
                </div>
              </div>

              <div className="rounded-xl bg-ink-surface/60 border border-ink-text/5 p-5">
                <div className="flex items-center gap-2 mono text-xs uppercase tracking-widest text-brand mb-3">
                  <GitBranch size={14} weight="bold" /> {t.techCore.formula.zcr}
                </div>
                <div className="font-heading font-semibold text-lg mb-2">
                  {t.techCore.factors.zcrTitle}
                </div>
                <div className="text-sm text-muted leading-relaxed">
                  {t.techCore.factors.zcrDesc}
                </div>
              </div>
            </div>

            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-brand/15 blur-[80px] pointer-events-none" />
          </div>

          {/* Right column — RL + GNN cards */}
          <div className="lg:col-span-5 flex flex-col gap-6">
            <div className="glass rounded-2xl p-7 relative overflow-hidden flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-brand/10 border border-brand/30 flex items-center justify-center">
                  <Brain size={20} weight="duotone" className="text-brand-cyan" />
                </div>
                <div>
                  <div className="font-heading font-semibold">{t.techCore.agentTitle}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted">
                    {t.techCore.agentSub}
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted leading-relaxed">
                {t.techCore.agentDesc}
              </p>
            </div>

            <div className="glass rounded-2xl p-7 relative overflow-hidden flex-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-brand-cyan/10 border border-brand-cyan/30 flex items-center justify-center">
                  <ChartLine size={20} weight="duotone" className="text-brand-cyan" />
                </div>
                <div>
                  <div className="font-heading font-semibold">{t.techCore.gnnTitle}</div>
                  <div className="mono text-[10px] uppercase tracking-widest text-muted">
                    {t.techCore.gnnSub}
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted leading-relaxed">
                {t.techCore.gnnDesc}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TechCore;
